import React, { useEffect, useState } from 'react';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function LegRow({ leg }) {
  const dte = leg.expiration_date
    ? Math.max(0, Math.ceil((new Date(leg.expiration_date).setHours(16,0,0,0) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 16, padding: '8px 16px 8px 40px', background: '#0f1520', borderBottom: '0.5px solid #1e2d3d', fontSize: 13, color: '#94a3b8' }}>
      <div>
        <span style={{ color: '#cbd5e1' }}>{leg.leg_type === 'SHORT_PUT' ? 'Short put' : 'Short call'}</span>
        <span style={{ marginLeft: 8, color: '#475569' }}>${parseFloat(leg.strike).toFixed(2)} · {formatDate(leg.expiration_date)}</span>
      </div>
      <div>{leg.contracts} contract{leg.contracts !== 1 ? 's' : ''}</div>
      <div>${parseFloat(leg.premium_actual || 0).toFixed(2)}/contract</div>
      <div>{leg.outcome === "OPEN" ? (dte !== null ? dte + " DTE" : "—") : "—"}</div>
      <div style={{ color: leg.outcome === 'OPEN' ? '#4ade80' : '#475569' }}>{leg.outcome}</div>
    </div>
  );
}

export default function History() {
  const [positions, setPositions] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetch('/api/positions/history', { credentials: 'include' })
      .then(r => r.json())
      .then(async data => {
        setPositions(data);
        const tickers = [...new Set(data.map(p => p.ticker))];
        if (tickers.length > 0) {
          const q = await fetch(`/api/quotes?symbols=${tickers.join(',')}`, { credentials: 'include' })
            .then(r => r.json()).catch(() => ({}));
          setQuotes(q);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <p className="loading">Loading history...</p>;

  const totalPremium = positions.reduce((s, p) => s + parseFloat(p.total_premium_collected || 0), 0);
  const closedPositions = positions.filter(p => p.status === 'CLOSED');
  const openPositions = positions.filter(p => p.status !== 'CLOSED');

  // Unrealized P&L on open share positions
  let unrealizedPL = 0;
  openPositions.forEach(p => {
    if (p.shares_held && quotes[p.ticker]) {
      const currentPrice = parseFloat(quotes[p.ticker]);
      const costBasis = parseFloat(p.cost_basis_per_share || 0);
      unrealizedPL += (currentPrice - costBasis) * parseInt(p.shares_held);
    }
  });

  const netPL = totalPremium + unrealizedPL;

  const peakCapital = positions.reduce((max, p) => Math.max(max, parseFloat(p.total_capital_at_risk || 0)), 0);
  const startDate = positions.length > 0 ? new Date(positions[positions.length - 1].opened_at) : null;
  const daysElapsed = startDate ? Math.ceil((new Date() - startDate) / (1000 * 60 * 60 * 24)) : 0;

  const annualizedPremium = peakCapital > 0 && daysElapsed > 0
    ? ((totalPremium / peakCapital) * (365 / daysElapsed) * 100).toFixed(1)
    : null;

  const annualizedTotal = peakCapital > 0 && daysElapsed > 0
    ? ((netPL / peakCapital) * (365 / daysElapsed) * 100).toFixed(1)
    : null;

  const plColor = netPL >= 0 ? '#4ade80' : '#fca5a5';
  const totalReturnColor = annualizedTotal !== null
    ? (parseFloat(annualizedTotal) >= 0 ? '#4ade80' : '#fca5a5')
    : '#94a3b8';

  return (
    <>
      <div className="topbar">
        <span className="page-title">Trade history</span>
        {startDate && <span className="timestamp">{daysElapsed} days tracked · started {formatDate(startDate)}</span>}
      </div>

      <div className="stats-grid" style={{ marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">Premium collected</div>
          <div className="stat-value" style={{ color: '#4ade80' }}>${totalPremium.toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Net P&L</div>
          <div className="stat-value" style={{ color: plColor }}>{netPL >= 0 ? '+' : ''}${netPL.toFixed(2)}</div>
          <div className="stat-sub">premium + unrealized share P&L</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Premium yield annualized</div>
          <div className="stat-value" style={{ color: annualizedPremium > 0 ? '#4ade80' : '#94a3b8' }}>
            {annualizedPremium ? annualizedPremium + '%' : '—'}
          </div>
          <div className="stat-sub">premium only ÷ peak capital</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total return annualized</div>
          <div className="stat-value" style={{ color: totalReturnColor }}>
            {annualizedTotal !== null ? (parseFloat(annualizedTotal) >= 0 ? '' : '') + annualizedTotal + '%' : '—'}
          </div>
          <div className="stat-sub">premium + share P&L ÷ peak capital</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Positions</div>
          <div className="stat-value">{closedPositions.length} closed · {openPositions.length} open</div>
        </div>
      </div>

      <div className="section-label">All positions</div>

      {positions.length === 0 ? (
        <p className="empty">No trade history yet.</p>
      ) : (
        <>
          <div className="table-header">
            <span className="th">Position</span>
            <span className="th">Opened</span>
            <span className="th">Closed</span>
            <span className="th">Days</span>
            <span className="th">Premium</span>
            <span className="th">Status</span>
          </div>
          {positions.map(p => {
            const premium = parseFloat(p.total_premium_collected || 0);
            const legs = p.legs || [];
            const isExpanded = expanded[p.position_id];
            return (
              <div key={p.position_id} style={{ marginBottom: 2 }}>
                <div className="position-row" onClick={() => legs.length > 0 && toggleExpand(p.position_id)} style={{ cursor: legs.length > 0 ? 'pointer' : 'default' }}>
                  <div>
                    <div className="ticker">{p.ticker}</div>
                    <div className="cell-secondary">{p.shares_held ? p.shares_held + ' shares' : 'Options'}</div>
                  </div>
                  <div className="cell-primary">{formatDate(p.opened_at)}</div>
                  <div className="cell-primary">{formatDate(p.closed_at)}</div>
                  <div className="cell-primary">{p.days_in_trade ?? '—'}</div>
                  <div className="premium">+${premium.toFixed(2)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge ${p.status === 'CLOSED' ? 'badge-closed' : p.status === 'OPEN_CALL' ? 'badge-call' : 'badge-put'}`}>
                      {p.status === 'CLOSED' ? 'Closed' : p.status === 'OPEN_CALL' ? 'Open call' : p.status === 'OPEN_PUT' ? 'Open put' : p.status}
                    </span>
                    {legs.length > 0 && <span style={{ fontSize: 12, color: '#475569' }}>{isExpanded ? '▲' : '▼'}</span>}
                  </div>
                </div>
                {isExpanded && legs.length > 0 && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 16, padding: '6px 16px 6px 40px', background: '#0a0f18', fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <span>Contract</span><span>Size</span><span>Premium</span><span>DTE</span><span>Status</span>
                    </div>
                    {legs.map(l => <LegRow key={l.leg_id} leg={l} />)}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
